// A saved V2 review cites its evidence by fact id (C.<campaign>, INV.gap.<campaign>, K.3 …). Those ids are
// for the checks and the evidence store; a reader sees names. Text is shown without the bracketed citations
// (the evidence is one click away), and an id named in running text becomes what it refers to.
const KINDS = {
  'INV.gap': 'Downstream check gap', 'INV.chan': 'Channels buying little', 'INV.room': 'Budget headroom',
  'INV.loc': 'Where the change happened', 'INV.since': 'Since the latest change', 'INV.read': 'Change read',
  'INV.part': 'Part change read', 'INV.parts': 'Parts of the budget', 'INV.pshift': 'Part shifts', 'INV.query': 'Search terms',
  'INV.qmkt': 'Market search terms', 'INV.ladder': 'Budget response', 'INV.credit': 'Conversion credit', 'INV.note': 'Measurement note test',
  'INV.disp': 'Displacement', CH: 'Recorded change', NB: 'Network mix break', P: 'Budget pacing', POL: 'Policy and approvals',
  HYG: 'Keyword hygiene', LS: 'Learning status', K: 'Account context', STOP: 'Stop rule', M: 'Market', C: 'Campaign',
};
const ID = /\b(?:INV\.[a-z]+\.[\w-]+|BASIS(?:\.[\w.]+)?|SLR|A\.(?:check|recent|weekly|daily|cmp)|(?:CH|NB|P|POL|HYG|LS|K|STOP|M|C)\.[\w.-]+)\b/g;

export function factLabel(id, saved, marketName = code => code) {
  const names = saved.campaignNames || {};
  const text = String(id);
  if (/^A(\.|$)/.test(text)) return { A: 'Account totals', 'A.check': 'Account downstream check', 'A.recent': 'Account, recent days',
    'A.weekly': 'Account by week', 'A.daily': 'Account by day', 'A.cmp': 'Account comparison' }[text] || 'Account totals';
  if (/^BASIS/.test(text)) return 'Reporting basis';
  if (text === 'SLR') return 'Since the previous review';
  const inv = /^(INV\.[a-z]+)\.(.+)$/.exec(text);
  const [kind, rest] = inv ? [inv[1], inv[2]] : [text.split('.')[0], text.split('.').slice(1).join('.')];
  const label = KINDS[kind] || kind;
  const campaign = rest.split('.').find(part => names[part]);
  if (kind === 'C' && campaign) return names[campaign];
  if (kind === 'M') return marketName(rest);
  return campaign ? `${label}: ${names[campaign]}` : label;
}

// Google's network names as the review writes them.
const NETWORKS = { CONTENT: 'Display', SEARCH_PARTNERS: 'Search partners', SEARCH: 'Search', YOUTUBE: 'YouTube',
  YOUTUBE_WATCH: 'YouTube', YOUTUBE_SEARCH: 'YouTube search', DISCOVER: 'Discover', GMAIL: 'Gmail', MIXED: 'mixed networks' };

/** Prose for a reader: citations removed, ids named, network names as Google Ads shows them. */
// One citation: a fact id (C.123, INV.read.8, A, A.drivers, K.3) or an item id (R1, D1, X2, F1).
const CITE = String.raw`(?:[A-Z]|SLR|BASIS|[A-Z]{1,8}\.[\w.-]+|[RDXF]\d+)`;
// A run of bracketed citations, with whatever commas or semicolons the writer put between them: "[A], [K.7]".
const CITATIONS = new RegExp(String.raw`(?:[ \t]*[,;]?[ \t]*\[${CITE}(?:\s*[,;]\s*${CITE})*\])+`, 'g');
// The same ids written in parentheses, "(INV.read.1)" or "(D1)": references for the checker, not words for a reader.
// A campaign or market in parentheses, "(C.123)", is kept: it is renamed below to what it names.
const PAREN_IDS = new RegExp(String.raw`[ \t]*\((${CITE}(?:\s*[,;]\s*${CITE})*)\)`, 'g');
const NAMES_A_PLACE = /^(?:C\.\d+|M\.[\w-]+)$/;

export function plainText(text, saved, marketName) {
  return String(text || '')
    .replace(CITATIONS, '')
    .replace(PAREN_IDS, (whole, ids) => ids.split(/\s*[,;]\s*/).every(id => NAMES_A_PLACE.test(id)) ? whole : '')
    .replace(/\s*\(it has: [^)]*\)/g, '')
    // An account note named as a word in a sentence ("per K.10", "K.15's") reads as the notes it is.
    .replace(/\bK\.\d+'s\b/g, "the account notes'").replace(/\bK\.\d+\b/g, 'the account notes')
    .replace(/\bSTOP\.\d+\s*:\s*/g, '')
    .replace(/\bR\d+\b/g, 'the earlier recommendation')
    .replace(ID, id => factLabel(id, saved, marketName))
    .replace(/\b(CONTENT|SEARCH_PARTNERS|SEARCH|YOUTUBE_WATCH|YOUTUBE_SEARCH|YOUTUBE|DISCOVER|GMAIL|MIXED)\b/g, net => NETWORKS[net])
    .replace(/\s+([.,;:])/g, '$1')
    .trim();
}

export const SUMMARY_PARTS = [['overall', 'Overall'], ['working', 'What’s working'], ['concerns', 'Concerns'], ['next_steps', 'Next steps']];

/** The separate points in a summary part: a list as saved, or the sentences and clauses of older prose. */
export function summaryPoints(value, saved, marketName) {
  // A clause cut off at a semicolon gets the full stop it lost; a point written without one is left as written.
  const points = Array.isArray(value) ? value
    : String(value || '').split(/\n+|(?<=[.!?])\s+(?=[A-Z])/).flatMap(sentence => sentence.split(/;\s+/)
      .map((clause, n, all) => n < all.length - 1 ? `${clause.replace(/\s*\[[^\]]*\]\s*$/, '')}.` : clause));
  return points.map(point => plainText(point, saved, marketName).replace(/^[-•]\s*/, ''))
    .filter(Boolean).map(point => point[0].toUpperCase() + point.slice(1));
}

// Historical IDs are not stable across reviews. Name the subject from that
// follow-up itself, never from today's recommendation with the same R number.
export function followupTitle(row, saved) {
  const explicit = row.headline || row.title;
  if (typeof explicit === 'string' && explicit.trim()) return plainText(explicit, saved);
  const names = Object.values(saved.campaignNames || {}).filter(name => typeof name === 'string' && row.evidence?.includes(name));
  const readable = name => name.replace(/^(?:WPP_NRI_|NRI_Perf_)/, '').replace(/_/g, ' ');
  return names.length ? `Follow-up: ${[...new Set(names)].map(readable).join(', ')}` : 'Earlier recommendation follow-up';
}
